// src/pages/CreateNewPassword.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, TextField, Button, Paper, Alert } from '@mui/material';
import api from '../../services/api';
import { useModal } from '../../contexts/ModalContext';
import { useLoader } from '../../contexts/LoaderContext';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';

const CreateNewPassword = () => {
  const { openErrorModal, openSuccessModal } = useModal();
  const { uuid64, token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { showLoader, hideLoader } = useLoader();

  useEffect(() => {
    validateToken();
  }, [uuid64, token]);

  const PLAY_STORE_LINK = 'https://play.google.com/store/apps/details?id=com.salesgro_mobileapp';

  const validateToken = async () => {
    try {
      showLoader();
      const response = await api.get(`create_new_password/${uuid64}/${token}/`);
      if (response.status !== 200) {
        const errorData = response.data;
        openErrorModal("Token Validation Error", errorData.message || "Invalid token", () => navigate("/auth/login"));
      }
    } catch (error) {
      openErrorModal("Error", error.response?.data?.message || "Failed to validate token", () => navigate("/auth/login"));
    } finally {
      hideLoader();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords
    if (!password || !confirmPassword) {
      setError('Both fields are required');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    showLoader();
    
    try {
      const data = {
        new_password: password,
        confirm_password: confirmPassword
      };

      const response = await api.post(`create_new_password/${uuid64}/${token}/`, data);
      
      if (response.status === 200) {
        const handleRedirect = () => {
          const userAgent = navigator.userAgent || navigator.vendor || window.opera || '';
          const isMobileDevice = /android|iphone|ipad|ipod|opera mini|iemobile|wpdesktop/i.test(userAgent.toLowerCase());

          if (isMobileDevice) {
            window.location.href = PLAY_STORE_LINK;
          } else {
            navigate("/auth/login");
          }
        };

        openSuccessModal("Success", "Password updated successfully", handleRedirect);
      }
    } catch (error) {
      if (error.response?.data?.new_password) {
        setError(error.response.data.new_password[0]);
      } else {
        openErrorModal(
          "Error", 
          error.response?.data?.message || "Failed to update password", 
          () => navigate("/auth/login")
        );
      }
    } finally {
      hideLoader();
    }
  };

  return (
    <Paper
      elevation={2}
      sx={{
        width: '100%',
        p: { xs: 3, sm: 3 },
        borderRadius: 2,
        backgroundColor: 'white',
      }}
    >
      <Typography variant="h5" component="h1" align="center" fontWeight="medium" gutterBottom>
        Create New Password
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
        <TextField
          margin="dense"
          required
          fullWidth
          name="password"
          label="New Password"
          type={showPassword ? "text" : "password"}
          id="password"
          size="small"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword((show) => !show)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        
        <TextField
          margin="dense"
          required
          fullWidth
          name="confirmPassword"
          label="Confirm New Password"
          type={showConfirmPassword ? "text" : "password"}
          id="confirmPassword"
          size="small"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          sx={{ mb: 3 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowConfirmPassword((show) => !show)}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ 
            py: 1,
            backgroundColor: '#007050',
            '&:hover': {
              backgroundColor: '#00513a',
            },
          }}
        >
          Reset Password
        </Button>
      </Box>
    </Paper>
  );
};

export default CreateNewPassword;